import { apiFetch } from '../../../../utils/api';
import { getLogType } from '../../../../utils/diffUtils';

// View change details handler
export const handleViewChangeDetails = (
    activeVersionId,
    features,
    setActiveLogDetails,
    setLoadingLogDiff,
    setLogDiffEntry,
    setLogRulesetData
) => async (log) => {
    setActiveLogDetails(log);
    setLoadingLogDiff(true);
    setLogDiffEntry(null);
    setLogRulesetData(null);

    try {
        const logType = getLogType(log.activity);

        // 1. Only try to fetch branch diff if activeVersionId is set and is not the empty production GUID
        if (activeVersionId && activeVersionId !== '00000000-0000-0000-0000-000000000000') {
            try {
                const diffRes = await apiFetch(`/api/versions/${activeVersionId}/diff`);
                if (diffRes.ok) {
                    const diffData = await diffRes.json();
                    if (Array.isArray(diffData)) {
                        // Match by targetId/parentId or fall back to case-insensitive name match
                        const matchedDiff = diffData.find(d => 
                            ((d.id?.toString().toLowerCase() === log.targetId?.toString().toLowerCase() || 
                              d.parentId?.toString().toLowerCase() === log.targetId?.toString().toLowerCase()) ||
                             (d.name?.toString().toLowerCase() === log.item?.toString().toLowerCase())) && 
                            d.type === logType
                        );
                        if (matchedDiff) {
                            setLogDiffEntry(matchedDiff);
                            setLoadingLogDiff(false);
                            return;
                        }
                    }
                }
            } catch (diffErr) {
                console.warn('Failed to fetch version diff, falling back to current state:', diffErr);
            }
        }

        // 2. Fallback: Search commits history for a commit containing this change
        try {
            const commitsRes = await apiFetch(`/api/commits`);
            if (commitsRes.ok) {
                const commits = await commitsRes.json();
                const logTime = new Date(log.date).getTime();
                const candidateCommits = commits
                    .filter(c => c.changesJson)
                    .map(c => {
                        try {
                            const diffData = JSON.parse(c.changesJson);
                            if (Array.isArray(diffData)) {
                                const matchedDiff = diffData.find(d => 
                                    ((d.id?.toString().toLowerCase() === log.targetId?.toString().toLowerCase() || 
                                      d.parentId?.toString().toLowerCase() === log.targetId?.toString().toLowerCase()) ||
                                     (d.name?.toString().toLowerCase() === log.item?.toString().toLowerCase())) && 
                                    d.type === logType
                                );
                                if (matchedDiff) {
                                    const commitTime = new Date(c.reviewedAt || c.committedAt).getTime();
                                    const timeDiff = Math.abs(commitTime - logTime);
                                    return { commit: c, diff: matchedDiff, timeDiff };
                                }
                            }
                        } catch (e) {
                            return null;
                        }
                        return null;
                    })
                    .filter(item => item !== null);

                if (candidateCommits.length > 0) {
                    candidateCommits.sort((a, b) => a.timeDiff - b.timeDiff);
                    setLogDiffEntry({
                        ...candidateCommits[0].diff,
                        isHistorical: true,
                        commitComment: candidateCommits[0].commit.comment
                    });
                    setLoadingLogDiff(false);
                    return;
                }
            }
        } catch (commitErr) {
            console.warn('Failed to fetch commits history:', commitErr);
        }

        // 3. If not found in version diff (or in production), and it's a ruleset, fetch current grid details
        // TODO: This violates the rule "You need a family id to retrieve a ruleset"
        // Need to determine which family the ruleset belongs to, then use:
        // apiFetch(`/api/products/${familyId}/rulesets/${log.targetId}`)
        if (log.activity.includes('Ruleset') && log.targetId) {
            try {
                const rsRes = await apiFetch(`/api/rulesets/${log.targetId}`);
                if (rsRes.ok) {
                    const rsData = await rsRes.json();
                    setLogRulesetData(rsData);
                }
            } catch (rsErr) {
                console.warn('Failed to fetch ruleset details:', rsErr);
            }
        }
    } catch (err) {
        console.error('Error fetching change log details:', err);
    } finally {
        setLoadingLogDiff(false);
    }
};

// Revert mapping handler
export const handleRevertMapping = (
    selectedFamilyId,
    features,
    setSelectedFamilyId,
    fetchData
) => async (log) => {
    if (!window.confirm(`Are you sure you want to revert the change "${log.activity}" for "${log.item}"? This will create a new draft change in the current version context.`)) return;

    try {
        if (log.activity === 'Ruleset Created') {
            const res = await apiFetch(`/api/rulesets/${log.targetId}/unassign/${selectedFamilyId}`, {
                method: 'POST'
            });
            if (res.ok) {
                await fetchData();
            } else {
                alert(`Failed to unassign ruleset: ${await res.text()}`);
            }
        } else if (log.activity === 'Feature Created') {
            const targetFeat = features.find(f => f.id === log.targetId);
            if (!targetFeat) return;

            const currentFamilyIds = targetFeat.sourceFamilyIds || [];
            const updatedFamilyIds = currentFamilyIds
                .filter(id => id !== selectedFamilyId);

            const res = await apiFetch(`/api/features/scope/bulk`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify([{
                    id: targetFeat.id,
                    isGlobal: targetFeat.isGlobal,
                    mappedFamilyIds: updatedFamilyIds
                }])
            });

            if (res.ok) {
                await fetchData();
            } else {
                alert(`Failed to unassign feature: ${await res.text()}`);
            }
        } else if (log.activity === 'Ruleset Saved') {
            const res = await apiFetch(`/api/rulesets/${log.targetId}/revert-draft`, {
                method: 'POST'
            });
            if (res.ok) {
                await fetchData();
            } else {
                alert(`Failed to revert ruleset: ${await res.text()}`);
            }
        } else if (log.activity === 'Feature Saved') {
            const res = await apiFetch(`/api/features/${log.targetId}/revert-draft`, {
                method: 'POST'
            });
            if (res.ok) {
                await fetchData();
            } else {
                alert(`Failed to revert feature: ${await res.text()}`);
            }
        } else if (
            log.activity === 'Family Initialized' || log.activity === 'Family Renamed'
            || log.activity === 'Family Initialized' || log.activity === 'Family Renamed'
        ) {
            const res = await apiFetch(`/api/products/${selectedFamilyId}/revert-draft`, {
                method: 'POST'
            });
            if (res.ok) {
                const data = await res.json();
                if (data.deleted) {
                    setSelectedFamilyId(null);
                    await fetchData();
                } else {
                    await fetchData();
                }
            } else {
                alert(`Failed to revert family change: ${await res.text()}`);
            }
        }
    } catch (err) {
        console.error('Error reverting change', err);
        alert('Could not revert change.');
    }
};
